import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, Plus, RefreshCw, Search, ShoppingBag } from 'lucide-react';
import toast from 'react-hot-toast';
import { BasalamImportProductModal } from '../components/BasalamImportProductModal';
import { Pagination } from '../components/Pagination';
import { basalamService, type BasalamProductRow } from '../services/basalamService';
import { productService } from '../services/productService';
import { formatMoney } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import type { Category } from '../types';

const BASALAM_PRODUCT_URL = 'https://basalam.com/p';

export function BasalamProductsPage() {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<BasalamProductRow[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [importProduct, setImportProduct] = useState<BasalamProductRow | null>(null);
  const limit = 50;

  useEffect(() => {
    void productService.getCategories().then(setCategories).catch(() => {
      toast.error('خطا در دریافت دسته‌بندی‌ها');
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await basalamService.listProducts({ page, limit, q: query || undefined });
      setProducts(res.data ?? []);
      setTotal(res.total ?? 0);
      setTotalPages(Math.max(1, res.totalPages || 1));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت محصولات باسلام');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="h-7 w-7 text-indigo-600" />
            محصولات باسلام
          </h1>
          <p className="mt-1 text-slate-600 text-sm">
            موجودی و قیمت مستقیم از API باسلام (همه وضعیت‌ها)
            {!loading && total > 0 ? ` — ${total.toLocaleString('fa-IR')} محصول` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          بروزرسانی
        </button>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو بر اساس نام محصول..."
            className="w-full rounded-xl border border-slate-200 py-2.5 pr-10 pl-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
        <button
          type="submit"
          className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
        >
          جستجو
        </button>
        {query ? (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setQuery('');
              setPage(1);
            }}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50"
          >
            پاک کردن
          </button>
        ) : null}
      </form>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-right text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">محصول</th>
                <th className="px-4 py-3 font-semibold">قیمت</th>
                <th className="px-4 py-3 font-semibold">موجودی</th>
                <th className="px-4 py-3 font-semibold">وضعیت</th>
                <th className="px-4 py-3 font-semibold">لینک</th>
                <th className="px-4 py-3 font-semibold">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    در حال بارگذاری…
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    {query ? 'محصولی با این نام یافت نشد' : 'محصولی یافت نشد'}
                  </td>
                </tr>
              ) : (
                products.map((row, idx) => (
                  <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-500">{rowNumber(page, limit, idx)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{row.title || '—'}</div>
                      <div className="mt-0.5 text-xs text-slate-400">ID: {row.basalamProductId}</div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {formatMoney(row.price)} تومان
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          row.inventory <= 0
                            ? 'font-medium text-red-600'
                            : row.inventory <= 5
                              ? 'font-medium text-amber-600'
                              : 'font-medium text-slate-900'
                        }
                      >
                        {row.inventory.toLocaleString('fa-IR')} عدد
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!row.published && row.statusName ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">
                          {row.statusName}
                        </span>
                      ) : row.available ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-700">
                          {row.statusName || 'موجود'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs text-red-700">
                          <AlertCircle className="h-3 w-3" />
                          {row.statusName || 'ناموجود'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={`${BASALAM_PRODUCT_URL}/${row.basalamProductId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                        <span className="text-xs">باسلام</span>
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setImportProduct(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        افزودن به سایت
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100 p-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>

      <BasalamImportProductModal
        product={importProduct}
        categories={categories}
        onClose={() => setImportProduct(null)}
      />
    </div>
  );
}
